import { useEffect, useRef, useState, type ReactNode } from 'react';
import { clsx } from '../../../ds/clsx';
import { useIsMobile } from '../../../ds/hooks';
import { Popover, Sheet } from '../../../ds/components';
import { normalizeTimeInput } from '../../../lib/time-input';
import { addDays, todayKey } from '../format';
import { LaterPopover } from './fields';
import styles from './editor.module.css';

export type EditorTone = 'ink' | 'paper' | 'success' | 'quiet';

export interface EditorCardProps {
  tone: EditorTone;
  /** Colour of the 8 px dot in the title bar (a token, e.g. `var(--accent)`). */
  dot: string;
  title: string;
  subtitle: string;
  /** A short hint left of the footer actions (what to enter or what happens); optional. */
  footnote?: string | undefined;
  /** The footer's primary control (plain or split button). */
  primary: ReactNode;
  onClose: () => void;
  /** Extra footer content on the left of the actions (e.g. a link box). */
  children: ReactNode;
  /** Names the card for assistive technology ("Anruf-Editor"). */
  label: string;
}

const TONE_CLASS: Record<EditorTone, string | undefined> = {
  ink: styles.barInk,
  paper: styles.barPaper,
  success: styles.barSuccess,
  quiet: styles.barQuiet,
};

/** The mini-app card of the record (E1–E8): title bar with tone and dot, body, sunken footer with „Abbrechen" + primary. */
export function EditorCard({
  tone,
  dot,
  title,
  subtitle,
  footnote,
  primary,
  onClose,
  children,
  label,
}: EditorCardProps) {
  const mobile = useIsMobile();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (mobile) return;
    ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [mobile]);

  const body = (
    <>
      <div className={styles.body}>{children}</div>
      <div className={styles.footer}>
        <span className={styles.footnote}>{footnote ?? null}</span>
        <span className={styles.footerActions}>
          <button type="button" className={styles.ghostSmall} onClick={onClose}>
            Abbrechen
          </button>
          {primary}
        </span>
      </div>
    </>
  );

  if (mobile) {
    return (
      <Sheet open onClose={onClose} title={title} subtitle={subtitle} height="full" label={label}>
        {body}
      </Sheet>
    );
  }

  return (
    <section ref={ref} className={styles.card} role="region" aria-label={label}>
      <div className={clsx(styles.bar, TONE_CLASS[tone])}>
        <span className={styles.dot} style={{ background: dot }} />
        <div className={styles.barText}>
          <div className={styles.barTitle}>{title}</div>
          <div className={styles.barSubtitle}>{subtitle}</div>
        </div>
        <span className={styles.barActions}>
          <button type="button" className={styles.barClose} aria-label="Editor schließen" onClick={onClose}>
            ×
          </button>
        </span>
      </div>
      {body}
    </section>
  );
}

/** The footer's primary button with the in-flight ring. */
export function PrimaryAction({
  children,
  loading = false,
  disabled = false,
  onClick,
}: {
  children: ReactNode;
  loading?: boolean | undefined;
  disabled?: boolean | undefined;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={styles.primarySmall}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      onClick={onClick}
    >
      {loading ? <span aria-hidden="true" className={styles.spinner} /> : null}
      {children}
    </button>
  );
}

/** The split „Senden ⌄" of the mail and WhatsApp editors; the caret opens „Später senden" (III.T1). */
export function SplitSendButton({
  loading = false,
  disabled = false,
  onSend,
  onSendLater,
}: {
  loading?: boolean | undefined;
  disabled?: boolean | undefined;
  onSend: () => void;
  onSendLater: (date: string, time: string) => void;
}) {
  const caretRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(() => addDays(todayKey(), 1));
  const [time, setTime] = useState('08:00');
  return (
    <span className={styles.split}>
      <button
        type="button"
        className={styles.splitMain}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        onClick={onSend}
      >
        {loading ? <span aria-hidden="true" className={styles.spinner} /> : null}
        Senden
      </button>
      <button
        ref={caretRef}
        type="button"
        className={clsx(styles.splitCaret, open && styles.splitCaretOpen)}
        disabled={disabled || loading}
        aria-label="Später senden"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((state) => !state)}
      >
        ⌄
      </button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={caretRef}
        placement="top-end"
        offset={4}
        variant="none"
        label="Später senden"
        className={styles.calendarPopover}
      >
        <LaterPopover
          date={date}
          time={time}
          onDateChange={setDate}
          onTimeChange={setTime}
          onCancel={() => setOpen(false)}
          onConfirm={() => {
            setOpen(false);
            onSendLater(date, normalizeTimeInput(time));
          }}
        />
      </Popover>
    </span>
  );
}
